import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { useAuthStore } from '../../store/useAuthStore';
import {
  createEventType,
  deleteEventType,
  listEventTypes,
  listEventTypesWithUsage,
  listMyColorOverrides,
  setMyColorOverride,
  updateEventType,
  type ColorTarget,
  type EventTypeFamily,
} from '../../services/calendar/eventTypeService';
import { buildColorMap, type ColorMap } from '../../services/calendar/eventColors';
import { useTaskTemplates } from './useTaskTemplates';

const KEY = ['event-types'] as const;
const OVERRIDES_KEY = ['color-overrides'] as const;

export function useEventTypes() {
  return useQuery({ ...SEMI_FRESH, queryKey: [...KEY, 'list'], queryFn: listEventTypes });
}

export function useEventTypesWithUsage() {
  return useQuery({ ...SEMI_FRESH, queryKey: [...KEY, 'usage'], queryFn: listEventTypesWithUsage });
}

export function useCreateEventType() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id) ?? null;
  return useMutation({
    mutationFn: (input: { family: EventTypeFamily; name: string; color: string }) =>
      createEventType({ ...input, createdBy: collaboratorId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string; color?: string; is_active?: boolean } }) =>
      updateEventType(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteEventType,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMyColorOverrides() {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);
  return useQuery({
    ...SEMI_FRESH,
    queryKey: [...OVERRIDES_KEY, collaboratorId ?? ''],
    queryFn: () => listMyColorOverrides(collaboratorId!),
    enabled: !!collaboratorId,
  });
}

export function useSetMyColorOverride() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);
  return useMutation({
    mutationFn: ({ target, color }: { target: ColorTarget; color: string | null }) => {
      if (!collaboratorId) throw new Error('Session expirée');
      return setMyColorOverride(collaboratorId, target, color);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: OVERRIDES_KEY }),
  });
}

/** Couleur de chaque type d'événement et tâche type, pour la personne connectée. */
export function useColorMap(): ColorMap {
  const { data: eventTypes } = useEventTypes();
  const { data: templates } = useTaskTemplates(false);
  const { data: overrides } = useMyColorOverrides();
  return useMemo(
    () => buildColorMap(eventTypes ?? [], templates ?? [], overrides ?? []),
    [eventTypes, templates, overrides],
  );
}
